import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Github, ExternalLink, Folder, Save, Lock, CheckCircle2, GitCommit, GitPullRequest } from "lucide-react";

export default function IdeaLinksPanel({ idea, onRefresh, endpoint }) {
  const saveEndpoint = endpoint || `/ideas/${idea.id}/links`;
  const [links, setLinks] = useState(idea.links || { github_url: "", drive_folder_url: "", additional_links: [] });
  const [github, setGithub] = useState(null);
  const [drive, setDrive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ghStatus, setGhStatus] = useState({ connected: false, login: null });
  const [ghConnecting, setGhConnecting] = useState(false);
  const [ghToken, setGhToken] = useState("");
  const [ghModal, setGhModal] = useState(false);
  const [ghActivity, setGhActivity] = useState({ commits: [], pulls: [] });

  useEffect(() => {
    api.get("/integrations/github/status").then((r) => setGhStatus(r.data || { connected: false })).catch(() => {});
    // Handle OAuth callback flash-messages
    const p = new URLSearchParams(window.location.search);
    const g = p.get("github");
    if (g === "connected") {
      toast.success("GitHub connected");
      // Clean the query string so a page refresh doesn't re-toast
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", clean);
    } else if (g === "error" || g === "badstate") {
      toast.error("GitHub connect failed — please try again");
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const refetchGithub = () => {
    if (!links.github_url) return;
    api.get(`/integrations/github/repo?url=${encodeURIComponent(links.github_url)}`)
      .then((r) => setGithub(r.data)).catch(() => setGithub(null));
    api.get(`/integrations/github/activity?url=${encodeURIComponent(links.github_url)}`)
      .then((r) => setGhActivity(r.data || { commits: [], pulls: [] }))
      .catch(() => setGhActivity({ commits: [], pulls: [] }));
  };

  useEffect(() => {
    if (links.github_url) refetchGithub();
    if (links.drive_folder_url) {
      api.get(`/integrations/drive/folder?url=${encodeURIComponent(links.drive_folder_url)}`)
        .then((r) => setDrive(r.data)).catch(() => setDrive(null));
    }
    // eslint-disable-next-line
  }, [links.github_url, links.drive_folder_url, ghStatus.connected]);

  const connectGh = async () => {
    if (!ghToken.trim()) { toast.error("Paste your Personal Access Token"); return; }
    setGhConnecting(true);
    try {
      const r = await api.post("/integrations/github/connect", { token: ghToken.trim() });
      setGhStatus({ connected: true, login: r.data?.login });
      setGhToken(""); setGhModal(false);
      toast.success(`GitHub connected as @${r.data?.login}`);
      refetchGithub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "GitHub connect failed");
    } finally { setGhConnecting(false); }
  };

  // Preferred flow: full OAuth redirect. Falls back to the PAT modal only if
  // the backend isn't configured with an OAuth app.
  const startGhOAuth = async () => {
    try {
      const here = window.location.pathname + window.location.search;
      const r = await api.get(`/integrations/github/oauth/start?redirect_to=${encodeURIComponent(here)}`);
      if (r.data?.authorize_url) {
        window.location.href = r.data.authorize_url;
        return;
      }
      setGhModal(true);
    } catch (e) {
      // 503 → OAuth not configured → fall back to PAT modal
      setGhModal(true);
    }
  };
  const disconnectGh = async () => {
    if (!window.confirm("Disconnect GitHub? Private repo previews will stop working.")) return;
    try {
      await api.delete("/integrations/github/connect");
      setGhStatus({ connected: false, login: null });
      toast.success("GitHub disconnected");
    } catch { toast.error("Couldn't disconnect"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(saveEndpoint, links);
      toast.success("Links saved");
      setEditing(false);
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="kb-card p-6" data-testid="idea-links-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DATASETS · CODE · FILES</div>
        <div className="flex items-center gap-3">
          {ghStatus.connected ? (
            <button
              onClick={disconnectGh}
              className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1 hover:text-rose-600"
              title="Disconnect GitHub"
              data-testid="github-disconnect-btn"
            >
              <CheckCircle2 className="w-3 h-3" /> GitHub · @{ghStatus.login}
            </button>
          ) : (
            <button
              onClick={startGhOAuth}
              className="kb-mono text-[10px] uppercase tracking-wider text-white bg-kb-ink hover:bg-black px-2.5 py-1 rounded-sm inline-flex items-center gap-1"
              data-testid="github-connect-btn"
            >
              <Github className="w-3 h-3" /> Connect with GitHub
            </button>
          )}
          <button onClick={() => setEditing(!editing)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary" data-testid="links-edit-toggle">
            {editing ? "CANCEL" : "EDIT"}
          </button>
        </div>
      </div>

      {ghModal && (
        <div className="fixed inset-0 z-[160] bg-black/40 flex items-center justify-center p-4" data-testid="github-connect-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-kb-primary" />
              <h3 className="font-serif text-lg text-kb-ink">Authorize GitHub</h3>
            </div>
            <p className="text-xs text-kb-ink-muted mb-3">
              Paste a Personal Access Token (classic) with <code>repo</code> and <code>read:user</code> scopes so we can preview private repos you have access to. Create one at{" "}
              <a href="https://github.com/settings/tokens/new" target="_blank" rel="noreferrer" className="text-kb-primary underline">github.com/settings/tokens</a>.
            </p>
            <input
              className="kb-input text-sm w-full font-mono"
              placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={ghToken}
              onChange={(e) => setGhToken(e.target.value)}
              data-testid="github-token-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setGhModal(false); setGhToken(""); }} className="kb-btn-outline text-xs">Cancel</button>
              <button onClick={connectGh} disabled={ghConnecting} className="kb-btn-primary text-xs" data-testid="github-connect-save">
                {ghConnecting ? "Verifying…" : "Connect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing ? (
        <div className="mt-4 space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-2"><Github className="w-3 h-3" /> GitHub repo URL</label>
            <input className="kb-input mt-1.5 text-sm" placeholder="https://github.com/owner/repo"
              value={links.github_url || ""} onChange={(e) => setLinks({ ...links, github_url: e.target.value })}
              data-testid="links-github-input" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-2"><Folder className="w-3 h-3" /> Google Drive folder URL</label>
            <input className="kb-input mt-1.5 text-sm" placeholder="https://drive.google.com/drive/folders/…"
              value={links.drive_folder_url || ""} onChange={(e) => setLinks({ ...links, drive_folder_url: e.target.value })}
              data-testid="links-drive-input" />
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">Folder must be shared &apos;Anyone with link can view&apos; to embed.</div>
          </div>
          <button onClick={save} disabled={saving} className="kb-btn-primary text-sm" data-testid="links-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save links"}
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {github ? (
            <div className="border border-slate-200 rounded-sm p-4" data-testid="github-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Github className="w-4 h-4 text-kb-ink" />
                  <a href={github.html_url} target="_blank" rel="noreferrer" className="text-kb-primary font-medium text-sm">{github.full_name}</a>
                </div>
                <a href={github.html_url} target="_blank" rel="noreferrer" className="text-kb-ink-muted"><ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
              <div className="text-sm text-kb-ink-muted mt-2">{github.description || "—"}</div>
              <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                <div><div className="kb-mono text-[10px] text-kb-ink-light">★ STARS</div><div className="font-mono text-kb-primary">{github.stars}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">FORKS</div><div className="font-mono text-kb-primary">{github.forks}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">LANG</div><div className="font-mono text-kb-ink">{github.language || "—"}</div></div>
                <div><div className="kb-mono text-[10px] text-kb-ink-light">ISSUES</div><div className="font-mono text-kb-primary">{github.open_issues}</div></div>
              </div>

              {(ghActivity.commits?.length > 0 || ghActivity.pulls?.length > 0) && (
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {ghActivity.commits?.length > 0 && (
                    <div data-testid="github-commits">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1 mb-1.5">
                        <GitCommit className="w-3 h-3" /> Recent commits
                      </div>
                      <ul className="space-y-1.5">
                        {ghActivity.commits.map((c) => (
                          <li key={c.sha} className="text-xs">
                            <a href={c.html_url} target="_blank" rel="noreferrer" className="kb-mono text-[10px] text-kb-primary">{c.sha}</a>
                            <span className="ml-2 text-kb-ink truncate inline-block max-w-[220px] align-middle">{c.message}</span>
                            {c.author && <div className="kb-mono text-[10px] text-kb-ink-light ml-2">{c.author}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ghActivity.pulls?.length > 0 && (
                    <div data-testid="github-pulls">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1 mb-1.5">
                        <GitPullRequest className="w-3 h-3" /> Open pull requests
                      </div>
                      <ul className="space-y-1.5">
                        {ghActivity.pulls.map((p) => (
                          <li key={p.number} className="text-xs">
                            <a href={p.html_url} target="_blank" rel="noreferrer" className="text-kb-primary">#{p.number}</a>
                            <span className="ml-2 text-kb-ink">{p.title}</span>
                            {p.draft && <span className="ml-1 text-[10px] text-amber-600">· draft</span>}
                            {p.user && <div className="kb-mono text-[10px] text-kb-ink-light ml-2">by {p.user}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (links.github_url ? <div className="text-sm text-kb-ink-muted">Loading GitHub metadata…</div> : <div className="text-sm text-kb-ink-muted">No GitHub repo linked yet.</div>)}

          {drive ? (
            <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="drive-card">
              <div className="px-4 py-3 flex items-center justify-between border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Folder className="w-4 h-4 text-kb-ink" />
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">GOOGLE DRIVE FOLDER</span>
                </div>
                <a href={drive.open_url} target="_blank" rel="noreferrer" className="text-kb-ink-muted"><ExternalLink className="w-3.5 h-3.5" /></a>
              </div>
              <iframe src={drive.embed_url} title="Drive folder" className="w-full" style={{ height: 320 }} data-testid="drive-iframe" />
            </div>
          ) : (links.drive_folder_url ? <div className="text-sm text-kb-ink-muted">Loading Drive folder…</div> : <div className="text-sm text-kb-ink-muted">No Drive folder linked yet.</div>)}
        </div>
      )}
    </div>
  );
}
